import React from 'react';
import { motion } from 'motion/react';
import { Star, MessageSquareQuote, TrendingUp, Users, MapPin, Globe, CheckCircle2 } from 'lucide-react';

import { collection, onSnapshot, doc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const DEFAULT_STATS = [
  { id: 'kmCompleted', label: 'KM COMPLETED', value: '300+', icon: <TrendingUp className="h-4 w-4" /> },
  { id: 'expeditions', label: 'EXPEDITIONS', value: '50+', icon: <MapPin className="h-4 w-4" /> },
  { id: 'members', label: 'MEMBERS', value: '15+', icon: <Users className="h-4 w-4" /> },
  { id: 'countries', label: 'COUNTRIES', value: '3', icon: <Globe className="h-4 w-4" /> }
];

const STATIC_TESTIMONIALS = [
  {
    quote: "The OWS EXPEDITION CX2 changed how I see the lake. It's not a playground anymore; it's a frontier. The Syndicate training made the crossing possible.",
    author: "MARK T.",
    role: "ELITE MEMBER",
    avatar: "https://i.pravatar.cc/150?u=markt"
  },
  {
    quote: "Our corporate leadership team bonding in the open water was the most transformative experience we've had. High stakes, high trust.",
    author: "SARAH L.",
    role: "CEO, TECH SOLUTIONS",
    avatar: "https://i.pravatar.cc/150?u=sarahl"
  }
];

const SocialProof: React.FC = () => {
  const [stats, setStats] = React.useState(DEFAULT_STATS);
  const [dynamicReviews, setDynamicReviews] = React.useState<any[]>([]);

  React.useEffect(() => {
    const statsUnsub = onSnapshot(doc(db, 'site_stats', 'main'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setStats(prev => prev.map(s => ({
          ...s,
          value: data[s.id] || s.value
        })));
      }
    }, (err) => console.error("SocialProof stats snapshot error:", err));

    const reviewsQuery = query(
      collection(db, 'reviews'),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc')
    );

    const reviewsUnsub = onSnapshot(reviewsQuery, (snap) => {
      const reviews = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDynamicReviews(reviews);
    }, (err) => console.error("SocialProof reviews snapshot error:", err));

    return () => {
      statsUnsub();
      reviewsUnsub();
    };
  }, []);

  const allTestimonials = dynamicReviews.map(r => ({
    quote: r.content,
    author: r.userName?.toUpperCase(),
    role: r.userTier || "SYNDICATE MEMBER",
    avatar: r.userAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(r.userName || 'U')}&background=0D9488&color=fff`,
    image: r.image
  }));

  return (
    <section className="py-24 bg-ows-white border-y border-[#141414]/10" id="social-proof">
      <div className="max-w-7xl mx-auto px-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-24">
          {stats.map((stat, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center p-8 border border-[#141414]/5 rounded-3xl hover:border-ows-teal/30 bg-ows-sand/10 transition-all duration-500"
            >
              <div className="flex justify-center mb-4 text-ows-teal">
                {stat.icon}
              </div>
              <h3 className="text-3xl md:text-4xl font-display font-light uppercase tracking-tight mb-2 text-[#141414]">
                {stat.value}
              </h3>
              <p className="text-[10px] font-mono font-bold text-[#141414]/40 uppercase tracking-[0.2em]">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-[#141414] mb-6">Member Dispatches</h2>
          <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-ows-teal">Verified Intelligence from the Field</p>
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {allTestimonials.map((t, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              viewport={{ once: true }}
              className="bg-white text-[#141414] border border-[#141414]/5 p-5 rounded-[1.5rem] shadow-sm relative overflow-hidden group hover:border-ows-teal/30 hover:shadow-md transition-all duration-500 flex flex-col"
            >
              <div className="flex-grow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5 text-ows-teal">
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-2 w-2 fill-current" />)}
                  </div>
                  {t.image && (
                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-[#141414]/5 shadow-inner">
                      <img 
                        src={t.image} 
                        alt="Dispatch" 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                </div>
                
                <p className="text-[11px] md:text-xs font-display font-light leading-relaxed mb-4 text-[#141414]/80 italic font-serif tracking-wide">
                  "{t.quote}"
                </p>
              </div>
              
              <div className="flex items-center gap-3 pt-4 border-t border-[#141414]/5">
                <div className="relative shrink-0">
                   <div className="w-8 h-8 rounded-full border border-ows-teal/10 bg-ows-sand overflow-hidden">
                    <img 
                      src={t.avatar} 
                      alt={t.author} 
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 bg-ows-teal text-white p-0.5 rounded-full shadow-sm">
                    <CheckCircle2 className="w-1.5 h-1.5" />
                  </div>
                </div>
                <div className="min-w-0">
                  <h4 className="text-[8px] font-mono font-bold uppercase tracking-widest text-[#141414] truncate">{t.author}</h4>
                  <p className="text-[7px] font-mono font-bold text-ows-teal uppercase tracking-widest mt-0.5 truncate">{t.role}</p>
                </div>
              </div>

              <MessageSquareQuote className="absolute -bottom-2 -right-2 h-10 w-10 opacity-[0.03] -rotate-12 group-hover:rotate-0 transition-transform duration-700" />
            </motion.div>
          ))}
          
          {allTestimonials.length === 0 && (
            <div className="col-span-full py-24 text-center bg-ows-sand/10 rounded-[3rem] border border-dashed border-[#141414]/10">
              <p className="text-sm text-[#141414]/30 font-serif italic">"The dispatch feed is currently encrypted. Waiting for member transmissions."</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
